import * as React from "react";
import DarkModeIcon from "@mui/icons-material/DarkModeRounded";
import LightModeIcon from "@mui/icons-material/LightModeRounded";
import Box from "@mui/material/Box";
import IconButton, {IconButtonOwnProps} from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {alpha, Theme, useColorScheme} from "@mui/material/styles";
import {gray} from "@/theme/themePrimitives";
import {svgIconClasses} from "@mui/material/SvgIcon";

export default function ColorModeIconDropdown(props: IconButtonOwnProps) {
    const {mode, systemMode, setMode} = useColorScheme();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const handleMode = (targetMode: "system" | "light" | "dark") => () => {
        setMode(targetMode);
        handleClose();
    };
    if (!mode) {
        return (
            <Box
                data-screenshot="toggle-mode"
                sx={(theme) => ({
                    verticalAlign: "bottom",
                    display: "inline-flex",
                    width: "2.25rem",
                    height: "2.25rem",
                    borderRadius: (theme.vars || theme).shape.borderRadius,
                    border: "1px solid",
                    borderColor: (theme.vars || theme).palette.divider,
                })}
            />
        );
    }
    const resolvedMode = (systemMode || mode) as "light" | "dark";
    const icon = {
        light: <LightModeIcon/>,
        dark: <DarkModeIcon/>,
    }[resolvedMode];
    return (
        <React.Fragment>
            <IconButton
                data-screenshot="toggle-mode"
                onClick={handleClick}
                disableRipple
                size="small"
                sx={(theme: Theme) => ({
                    boxShadow: "none",
                    borderRadius: (theme.vars || theme).shape.borderRadius,
                    textTransform: "none",
                    fontWeight: theme.typography.fontWeightMedium,
                    letterSpacing: 0,
                    color: (theme.vars || theme).palette.text.primary,
                    border: "1px solid",
                    borderColor: gray[200],
                    backgroundColor: alpha(gray[50], 0.3),
                    "&:hover": {
                        backgroundColor: gray[100],
                        borderColor: gray[300],
                    },
                    "&:active": {
                        backgroundColor: gray[200],
                    },
                    ...theme.applyStyles("dark", {
                        backgroundColor: gray[800],
                        borderColor: gray[700],
                        "&:hover": {
                            backgroundColor: gray[900],
                            borderColor: gray[600],
                        },
                        "&:active": {
                            backgroundColor: gray[900],
                        },
                    }),
                    width: "2.5rem",
                    // height: "2.25rem",
                    padding: "0.25rem",
                    [`& .${svgIconClasses.root}`]: {fontSize: "1rem"},
                })}
                aria-controls={open ? "color-scheme-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                {...props}
            >
                {icon}
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                slotProps={{
                    paper: {
                        variant: "outlined",
                        elevation: 0,
                        sx: {
                            my: "4px",
                        },
                    },
                }}
                transformOrigin={{horizontal: "right", vertical: "top"}}
                anchorOrigin={{horizontal: "right", vertical: "bottom"}}
            >
                <MenuItem selected={mode === "system"} onClick={handleMode("system")}>
                    默认
                </MenuItem>
                <MenuItem selected={mode === "light"} onClick={handleMode("light")}>
                    浅色
                </MenuItem>
                <MenuItem selected={mode === "dark"} onClick={handleMode("dark")}>
                    深色
                </MenuItem>
            </Menu>
        </React.Fragment>
    );
}
